import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, of } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface GoogleLocationSuggestion {
  placeId: string;
  description: string;
}

export interface GoogleParsedLocation {
  formattedAddress: string;
  addressLine: string;
  city: string;
  region: string;
  country: string;
  countryCode: string;
  postalCode: string;
  latitude: number | null;
  longitude: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class GoogleLocationService {
  constructor(private readonly http: HttpClient) {}

  get enabled(): boolean {
    return this.apiKey.length > 0;
  }

  async searchSuggestions(
    input: string,
    countryCode?: string
  ): Promise<GoogleLocationSuggestion[]> {
    const query = `${input || ''}`.trim();
    if (!query || !this.enabled) {
      return [];
    }

    let params = new HttpParams()
      .set('input', query)
      .set('types', 'geocode')
      .set('key', this.apiKey);

    const country = `${countryCode || ''}`.trim().toLowerCase();
    if (country) {
      params = params.set('components', `country:${country}`);
    }

    const response = await firstValueFrom(
      this.http
        .get<any>('https://maps.googleapis.com/maps/api/place/autocomplete/json', {
          params,
        })
        .pipe(
          map((payload) => {
            const rows = Array.isArray(payload?.predictions)
              ? payload.predictions
              : [];

            return rows
              .map((row: any) => ({
                placeId: `${row?.place_id || ''}`.trim(),
                description: `${row?.description || ''}`.trim(),
              }))
              .filter(
                (row: GoogleLocationSuggestion) =>
                  row.placeId.length > 0 && row.description.length > 0
              );
          }),
          catchError(() => of([]))
        )
    );

    return response;
  }

  async getPlaceDetails(placeId: string): Promise<GoogleParsedLocation | null> {
    const id = `${placeId || ''}`.trim();
    if (!id || !this.enabled) {
      return null;
    }

    const params = new HttpParams()
      .set('place_id', id)
      .set(
        'fields',
        'address_component,formatted_address,geometry/location'
      )
      .set('key', this.apiKey);

    const response = await firstValueFrom(
      this.http
        .get<any>('https://maps.googleapis.com/maps/api/place/details/json', {
          params,
        })
        .pipe(catchError(() => of(null)))
    );

    const result = response?.result;
    if (!result || typeof result !== 'object') {
      return null;
    }

    const components = Array.isArray(result.address_components)
      ? result.address_components
      : [];

    const country = this.componentByType(components, 'country');
    const region =
      this.componentByType(components, 'administrative_area_level_1') ||
      this.componentByType(components, 'administrative_area_level_2');
    const city =
      this.componentByType(components, 'locality') ||
      this.componentByType(components, 'postal_town') ||
      this.componentByType(components, 'sublocality') ||
      this.componentByType(components, 'administrative_area_level_2');
    const postalCode = this.componentByType(components, 'postal_code');
    const streetNumber = this.componentByType(components, 'street_number');
    const route = this.componentByType(components, 'route');
    const addressLine = [streetNumber, route].filter(Boolean).join(' ').trim();

    const lat = Number(result?.geometry?.location?.lat);
    const lng = Number(result?.geometry?.location?.lng);

    const countryCodeComponent = components.find((item: any) =>
      Array.isArray(item?.types) && item.types.includes('country')
    );
    const countryCode = `${countryCodeComponent?.short_name || ''}`.trim();

    return {
      formattedAddress: `${result?.formatted_address || ''}`.trim(),
      addressLine,
      city,
      region,
      country,
      countryCode,
      postalCode,
      latitude: Number.isFinite(lat) ? lat : null,
      longitude: Number.isFinite(lng) ? lng : null,
    };
  }

  private get apiKey(): string {
    return `${(environment as any).googlePlacesApiKey || ''}`.trim();
  }

  private componentByType(components: any[], type: string): string {
    const component = components.find(
      (item) => Array.isArray(item?.types) && item.types.includes(type)
    );
    return `${component?.long_name || ''}`.trim();
  }
}
